import { Form, Input, Modal, Radio, Select, message } from 'antd';
import { omsOrderRecycleOrderUpdateInfoV2 } from 'apis/oms';
import i18n from '../../../i18n';
import LOCALS from '../../../commons/locals';
import { useEffect, useMemo } from 'react';
import { OmsRecycleOrderDetail } from 'types/oms';
import { GlobalInfoState } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import {
  buildModifyBaseInfoPayload,
  getModifyBaseInfoInitialValues,
  getSelectableStaffOptions,
} from './modify-base-info.utils';

type Props = {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  data: OmsRecycleOrderDetail;
  staffSelectOptions: GlobalInfoState['staffSelectOptions'];
};

const ModifyBaseInfo = ({
  open,
  onCancel,
  onSuccess,
  data,
  staffSelectOptions,
}: Props) => {
  const [form] = Form.useForm();
  const { shop, subShop } = useAppSelector(selectUserInfo);
  const initialValues = useMemo(() => {
    const values = getModifyBaseInfoInitialValues(data);
    if (!values.receptionistLabel && values.receptionistId !== undefined) {
      values.receptionistLabel = staffSelectOptions.find(
        (o) => o.value === values.receptionistId,
      )?.label;
    }
    return values;
  }, [data, staffSelectOptions]);
  const currentShopIdList = useMemo(
    () =>
      [shop, ...subShop].filter(
        (shopId): shopId is number => shopId !== undefined,
      ),
    [shop, subShop],
  );
  const selectableStaffOptions = useMemo(
    () =>
      getSelectableStaffOptions(
        staffSelectOptions,
        initialValues,
        currentShopIdList,
      ),
    [staffSelectOptions, initialValues, currentShopIdList],
  );
  const staffNameOptions = useMemo(
    () =>
      selectableStaffOptions.map((option) => ({
        value: option.label,
        label: option.label,
      })),
    [selectableStaffOptions],
  );

  useEffect(() => {
    if (open && data) {
      form.setFieldsValue(initialValues);
    }
  }, [open, data, form, initialValues]);

  const handleOk = async () => {
    const values = await form.validateFields();
    const omsRecycleOrderId = data.omsRecycleOrder?.id;
    if (!omsRecycleOrderId) return;

    await omsOrderRecycleOrderUpdateInfoV2(
      buildModifyBaseInfoPayload(values, String(omsRecycleOrderId)),
    );

    message.success(i18n.t(LOCALS.successful_operation));
    onSuccess();
  };

  return (
    <Modal
      open={open}
      title={i18n.t(LOCALS.modify_basic_info)}
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
      width={560}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item name="receptionistId" label={i18n.t(LOCALS.reception_staff)}>
          <Select
            allowClear
            options={selectableStaffOptions}
            placeholder={i18n.t(LOCALS.please_select) || ''}
            showSearch
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          name="staffName"
          label={i18n.t(LOCALS.staff)}
          rules={[
            {
              required: true,
              message: i18n.t(LOCALS.select_representative) || '',
            },
          ]}
        >
          <Select
            options={staffNameOptions}
            placeholder={i18n.t(LOCALS.please_select) || ''}
            showSearch
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          name="isTradeIn"
          label={i18n.t(LOCALS.is_trade_in)}
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value={0}>{i18n.t(LOCALS.no)}</Radio>
            <Radio value={1}>{i18n.t(LOCALS.yes)}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="firstValuationShopRemark"
          label={i18n.t(LOCALS.service_comments)}
        >
          <Input.TextArea
            placeholder={i18n.t(LOCALS.please_enter) || ''}
            autoSize={{ minRows: 4, maxRows: 8 }}
            maxLength={1024}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModifyBaseInfo;
